import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Card, Empty, Form, InputNumber, Modal, Spin, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import PageHeader from '@/components/common/PageHeader';
import { ROUTESPATH } from '@/routes/constant';
import { IOnboardingAccount } from '../types';
import {
  useGetCustomerDiscountConfig,
  useCreateCustomerDiscountConfig,
  useUpdateCustomerDiscountConfig,
  usePatchAccountField,
  useGetOnboardingAccountDetail,
} from '../hooks';
import { useProductList } from '@/features/product-configurator/hooks/productSetup';
import { useAllConfigOptions } from '@/features/product-configurator/hooks/configOption';
import { useCategoryList } from '@/features/product-configurator/hooks/categorySetup';
import { CATEGORY_TYPE_OPTIONS } from '@/features/product-configurator/constants';
import { useGetProductConfigurationJson } from '@/features/product-configurator/hooks/productConfiguration';
import { ALL_OPTION_TYPES } from '@/features/product-configurator/constants/optionTypes';
import { IPricingRule } from '@/features/product-configurator/types/pricingSetup';
import { useGetCurrentUser } from '@/hooks/app';


const { Text } = Typography;

type DiscountType = 'per_window' | 'per_configuration';

interface IItemDiscount {
  discountPercentage?: number | null;
  discountAmount?: number | null;
  priceAfterDiscount?: number | null; // user-entered target price; discountAmount is derived from this
}

interface IGroupHeaderRow {
  isGroupHeader: true;
  optionType: number;
  _key: string;
  catId: string;
  groupItems: any[];
}


const calcDiscountedPrice = (price: number, discount: IItemDiscount): number | null => {
  if (discount.discountPercentage != null)
    return Math.max(0, price - price * (discount.discountPercentage / 100));
  if (discount.discountAmount != null)
    return Math.max(0, price - discount.discountAmount);
  return null;
};

/** Max price from pricemodifier OR the highest price in dogma_pricelist */
const getEffectiveMaxPrice = (record: any): number | null => {
  const base = record.dogma_pricemodifier ?? 0;
  const list = parsePriceList(record.dogma_pricelist);
  const listMax = list.length > 0 ? Math.max(...list.map((p: any) => p.price)) : 0;
  const max = Math.max(base, listMax);
  return max > 0 ? max : null;
};

const parsePriceList = (json?: string | null): { conditions: { categoryId: string; optionIds: string[] }[]; price: number }[] => {
  if (!json) return [];
  try { return JSON.parse(json); } catch { return []; }
};

/** Expand each product into a Base row and, when it has an unglazed price, a second Unglazed row */
const buildProductDiscountRows = (products: any[]) => {
  const rows: any[] = [];
  for (const p of products) {
    const hasUnglazed = p.dogma_unglazedprice != null;
    rows.push({ ...p, _rowType: 'main', _key: `${p.productid}-main`, _rowSpan: hasUnglazed ? 2 : 1 });
    if (hasUnglazed) {
      rows.push({ ...p, _rowType: 'unglazed', _key: `${p.productid}-unglazed` });
    }
  }
  return rows;
};

// Build and download a JSON file in the browser
const downloadJson = (obj: object, filename: string) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const CustomerDiscountPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const account = location.state?.account as IOnboardingAccount | undefined;

  const [discountVersion, setDiscountVersion] = useState(1);
  const [discountType, setDiscountType] = useState<DiscountType>('per_window');
  const [excludingWindowProduct, setExcludingWindowProduct] = useState(false);
  const [fullPercentage, setFullPercentage] = useState<number | null>(null);
  const [fullAmount, setFullAmount] = useState<number | null>(null);
  // Single map for all item discounts — avoids stale-override bug when both maps share the same optionId
  const [itemDiscounts, setItemDiscounts] = useState<Record<string, IItemDiscount>>({});
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data: currentUserData } = useGetCurrentUser();
  const currentUserEmail = currentUserData?.currentUser?.internalemailaddress
    || currentUserData?.currentUser?.domainname
    || 'unknown';

  const { data: productsData, isLoading: productsLoading } = useProductList({
    queryParams: { current: 1, pageSize: 100, filter: '' },
  });
  const { data: allOptionsData, isLoading: optionsLoading } = useAllConfigOptions();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategoryList({
    current: 1, pageSize: 100,
  });
  const { data: existingConfig } = useGetCustomerDiscountConfig(accountId);
  const { data: accountDetail } = useGetOnboardingAccountDetail(accountId);
  const createDiscountMutation = useCreateCustomerDiscountConfig();
  const updateDiscountMutation = useUpdateCustomerDiscountConfig();
  const { data: liveConfigData } = useGetProductConfigurationJson();

  const patchAccountMutation = usePatchAccountField();
  const isExisting = !!existingConfig;
  const isSaving = createDiscountMutation.isPending || updateDiscountMutation.isPending;

  const activeProducts = productsData?.value?.filter(p => p.statecode === 0) || [];

  const pricingRules: IPricingRule[] = ((liveConfigData?.Config as any)?.pricingRules || []).filter(
    (r: IPricingRule) => r.statecode === 0 || r.statecode === undefined,
  );

  const pricedOptions = (allOptionsData || []).filter(
    (o: any) => o.statecode === 0 && (
      (o.dogma_pricemodifier !== undefined && o.dogma_pricemodifier !== null && o.dogma_pricemodifier !== 0) ||
      (o.dogma_pricelist && o.dogma_pricelist !== '[]' && o.dogma_pricelist !== '')
    )
  );

  const sortedCategories = [...(categoriesData?.value || [])].sort((a, b) => {
    const orderA = CATEGORY_TYPE_OPTIONS.findIndex(opt => opt.value === a.dogma_categorytype);
    const orderB = CATEGORY_TYPE_OPTIONS.findIndex(opt => opt.value === b.dogma_categorytype);
    return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
  });

  const categoryMap = Object.fromEntries(
    sortedCategories.map(c => [c.dogma_categoryid, c.dogma_name])
  );

  const optionNameMap = Object.fromEntries(
    (allOptionsData || []).map((o: any) => [o.dogma_configurationoptionsid, o.dogma_name])
  );

  const renderConditions = (conditions: { categoryId: string; optionIds: string[] }[]) => {
    if (!conditions?.length) return <Text type="secondary">—</Text>;
    return (
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
        {conditions.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && (
              <span className="text-[10px] font-bold text-blue-500 px-0.5">AND</span>
            )}
            <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
              {categoryMap[group.categoryId] || 'Unknown'}:
            </span>
            {group.optionIds.map((id, oi) => (
              <React.Fragment key={id}>
                {oi > 0 && <span className="text-[10px] text-gray-400">or</span>}
                <Tag color="default" className="!m-0 !text-[11px] !leading-snug">{optionNameMap[id] || id}</Tag>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const optionsByCategory: Record<string, typeof pricedOptions> = {};
  for (const cat of sortedCategories) {
    optionsByCategory[cat.dogma_categoryid] = [];
  }
  for (const opt of pricedOptions as any[]) {
    const catId = opt._dogma_category_value || opt.dogma_categoryid || 'uncategorised';
    if (!optionsByCategory[catId]) optionsByCategory[catId] = [];
    optionsByCategory[catId].push(opt);
  }
  for (const catId of Object.keys(optionsByCategory)) {
    if ((optionsByCategory[catId]?.length ?? 0) === 0) delete optionsByCategory[catId];
  }

  // Populate free delivery from live account data
  useEffect(() => {
    if (accountDetail?.dogma_deliverycharge !== undefined) {
      setFreeDelivery(!!accountDetail.dogma_deliverycharge);
    }
  }, [accountDetail]);

  useEffect(() => {
    if (!existingConfig?.discountConfig) return;
    try {
      const saved = existingConfig.discountConfig as any;

      // Unified format — always read top-level discount and questionFlow
      const typeMap: Record<string, DiscountType> = {
        full: 'per_window', quote_total: 'per_window', options_only: 'per_window',
        per_window_amount: 'per_window', per_window: 'per_window',
        per_option: 'per_configuration', per_configuration: 'per_configuration',
      };
      setDiscountVersion(typeof saved.version === 'number' ? saved.version : 1);
      setDiscountType(typeMap[saved.type] ?? 'per_window');
      setExcludingWindowProduct(!!saved.excludingWindowProduct);
      setFullPercentage(saved.discountPercentage > 0 ? saved.discountPercentage : null);
      setFullAmount(saved.discountAmount > 0 ? saved.discountAmount : null);
      // freeDelivery is loaded from accountDetail.dogma_deliverycharge, not from saved config

      // Restore all item discounts from questionFlow into a single map
      const combined: Record<string, IItemDiscount> = {};
      (saved.questionFlow || []).forEach((q: any) => {
        (q.options || []).forEach((opt: any) => {
          combined[opt.optionId] = {
            discountPercentage: opt.discountPercentage > 0 ? opt.discountPercentage : null,
            discountAmount: opt.discountAmount > 0 ? opt.discountAmount : null,
          };
          combined[`${opt.optionId}::unglazed`] = {
            discountPercentage: (opt.unglazedDiscountPercentage ?? 0) > 0 ? opt.unglazedDiscountPercentage : null,
            discountAmount: (opt.unglazedDiscountAmount ?? 0) > 0 ? opt.unglazedDiscountAmount : null,
          };
          // Restore per-pricelist discounts
          (opt.priceListDiscounts || []).forEach((pld: any) => {
            combined[`${opt.optionId}::pl::${pld.index}`] = {
              discountPercentage: (pld.discountPercentage ?? 0) > 0 ? pld.discountPercentage : null,
              discountAmount: (pld.discountAmount ?? 0) > 0 ? pld.discountAmount : null,
            };
          });
        });
      });
      // Restore pricing rule discounts
      (saved.pricingRuleDiscounts || []).forEach((prd: any) => {
        combined[`pr::${prd.ruleId}`] = {
          discountPercentage: (prd.discountPercentage ?? 0) > 0 ? prd.discountPercentage : null,
          discountAmount: (prd.discountAmount ?? 0) > 0 ? prd.discountAmount : null,
        };
      });
      setItemDiscounts(combined);
    } catch {
      // leave defaults
    }
  }, [existingConfig]);

  const buildDiscountJson = () => {
    // Single source of truth — no stale-override from two separate maps
    const allDiscounts = itemDiscounts;

    const rawConfig = liveConfigData?.Config as any;
    const liveQuestionFlow: any[] = rawConfig?.questionFlow || rawConfig?.questions || [];

    // Some options (e.g. window type: Arch/Bays/Coupled) expose a synthetic optionId
    // ("A0001", ...) that never carries a discount directly — the discount is set
    // against the underlying real product(s) instead (Products tab, keyed by productid).
    // Fall back to any of the option's validProducts to pull that discount in.
    // A key "has a discount" if it carries a direct discount, a pricelist
    // (price-rule) discount, or an unglazed discount — options priced purely
    // via pricelist conditions (e.g. "Inside Only", colour-dependent prices)
    // never get a direct discount entry, so checking only `allDiscounts[key]`
    // missed them entirely and silently dropped their discounts on save.
    const keyHasDiscount = (key: string): boolean => {
      const d = allDiscounts[key];
      const hasMainDiscount = !!d && (d.discountPercentage != null || d.discountAmount != null);
      const hasPlDiscount = Object.keys(allDiscounts).some(k => k.startsWith(`${key}::pl::`));
      const ud = allDiscounts[`${key}::unglazed`];
      const hasUnglazedDiscount = !!ud && (ud.discountPercentage != null || ud.discountAmount != null);
      return hasMainDiscount || hasPlDiscount || hasUnglazedDiscount;
    };

    const resolveDiscountKey = (opt: any): string | undefined => {
      if (keyHasDiscount(opt.optionId)) return opt.optionId;
      const validIds: string[] = opt.validProducts || [];
      return validIds.find(keyHasDiscount);
    };

    const questionFlow = liveQuestionFlow
      .map((q: any) => {
        const discountedOptions = (q.options || [])
          .map((opt: any) => ({ opt, discountKey: resolveDiscountKey(opt) }))
          .filter(({ discountKey }: any) => {
            if (!discountKey) return false;
            const d = allDiscounts[discountKey];
            const hasMainDiscount = d && (d.discountPercentage != null || d.discountAmount != null);
            const hasPlDiscount = Object.keys(allDiscounts).some(k => k.startsWith(`${discountKey}::pl::`));
            const ud = allDiscounts[`${discountKey}::unglazed`];
            const hasUnglazedDiscount = ud && (ud.discountPercentage != null || ud.discountAmount != null);
            return hasMainDiscount || hasPlDiscount || hasUnglazedDiscount;
          })
          .map(({ opt, discountKey }: any) => {
            const d = allDiscounts[discountKey] || {};
            const ud = allDiscounts[`${discountKey}::unglazed`] || {};
            const plKeys = Object.keys(allDiscounts).filter(k => k.startsWith(`${discountKey}::pl::`));
            const priceListDiscounts = plKeys
              .map(k => {
                const pd = allDiscounts[k];
                const idx = parseInt(k.split('::pl::')[1]);
                if (!pd || (pd.discountAmount == null && pd.discountPercentage == null)) return null;
                return {
                  index: idx,
                  discountAmount: pd.discountAmount ?? 0,
                  discountPercentage: pd.discountPercentage ?? 0,
                };
              })
              .filter(Boolean);
            return {
              optionId: opt.optionId,
              optionText: opt.optionText,
              discountPercentage: d.discountPercentage ?? 0,
              discountAmount: d.discountAmount ?? 0,
              unglazedDiscountPercentage: ud.discountPercentage ?? 0,
              unglazedDiscountAmount: ud.discountAmount ?? 0,
              ...(priceListDiscounts.length > 0 && { priceListDiscounts }),
            };
          });
        return discountedOptions.length > 0
          ? { questionId: q.questionId, question: q.question, options: discountedOptions }
          : null;
      })
      .filter(Boolean);

    const pricingRuleDiscounts = pricingRules
      .filter(r => {
        const d = allDiscounts[`pr::${r.dogma_pricingruleid}`];
        return d && (d.discountPercentage != null || d.discountAmount != null);
      })
      .map(r => {
        const d = allDiscounts[`pr::${r.dogma_pricingruleid}`] || {};
        return {
          ruleId: r.dogma_pricingruleid,
          ruleName: r.dogma_name,
          discountPercentage: d.discountPercentage ?? 0,
          discountAmount: d.discountAmount ?? 0,
        };
      });

    // Only the active type's discounts are persisted — switching tabs without
    // saving must not lose the other type's in-progress values, but saving as
    // one type does reset the other type's data on the server.
    const isPerWindow = discountType === 'per_window';
    return {
      version: discountVersion,
      type: discountType,
      discountPercentage: isPerWindow ? (fullPercentage ?? 0) : 0,
      discountAmount: isPerWindow ? (fullAmount ?? 0) : 0,
      excludingWindowProduct: isPerWindow ? excludingWindowProduct : false,
      questionFlow: isPerWindow ? [] : questionFlow,
      pricingRuleDiscounts: isPerWindow ? [] : pricingRuleDiscounts,
    };
  };

  const doSave = () => {
    if (!accountId) return;
    const nextVersion = discountVersion + 1;
    setDiscountVersion(nextVersion);
    const discountConfig = { ...buildDiscountJson(), version: nextVersion };
    const payload = {
      accountId,
      discountConfig,
      updatedAt: new Date().toISOString(),
      updatedBy: currentUserEmail,
    };
    const mutation = isExisting ? updateDiscountMutation : createDiscountMutation;
    mutation.mutate(payload, {
      onSuccess: () => {
        if (!isExisting) navigate(ROUTESPATH.ONBOARDING);
        // On update stay on the page
      },
    });
  };

  const handleSave = () => {
    if (!accountId) return;
    // Show confirmation modal when updating an existing config
    if (isExisting) {
      setShowConfirmModal(true);
    } else {
      doSave();
    }
  };

  const handleExport = () => {
    const json = buildDiscountJson();
    const date = new Date().toISOString().split('T')[0];
    const name = account?.name?.replace(/\s+/g, '-').toLowerCase() || accountId;
    downloadJson(json, `customer-discount-${name}-${date}.json`);
  };


  const setDiscountPct = (id: string, pct: number | null) =>
    setItemDiscounts(prev => ({ ...prev, [id]: { discountPercentage: pct, discountAmount: null, priceAfterDiscount: null } }));

  const setDiscountAmt = (id: string, amt: number | null) =>
    setItemDiscounts(prev => ({ ...prev, [id]: { discountPercentage: null, discountAmount: amt, priceAfterDiscount: null } }));

  const setDiscountPAD = (id: string, pad: number | null, basePrice: number) =>
    setItemDiscounts(prev => ({
      ...prev,
      [id]: {
        discountPercentage: null,
        discountAmount: pad != null ? Math.max(0, basePrice - pad) : null,
        priceAfterDiscount: pad,
      },
    }));

  const dataLoading = productsLoading || optionsLoading || categoriesLoading;
  const perWindowLoading = productsLoading;
  const perOptionsLoading = optionsLoading || categoriesLoading;

  const EXAMPLE_PRICE = 1000;
  const previewAfter = calcDiscountedPrice(EXAMPLE_PRICE, { discountPercentage: fullPercentage, discountAmount: fullAmount });
  const previewSaving = previewAfter != null ? EXAMPLE_PRICE - previewAfter : 0;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Update confirmation modal */}
      <Modal
        title="Update Discount Configuration"
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onOk={() => { setShowConfirmModal(false); doSave(); }}
        okText="Yes, Update"
        cancelText="Cancel"
        okButtonProps={{ loading: isSaving }}
      >
        <p>This account already has a discount configuration. Are you sure you want to overwrite it?</p>
      </Modal>

      <PageHeader
        title="Configure Custom Discount"
        subtitle={account?.name ? `Discount settings for ${account.name}` : 'Custom discount configuration'}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          location.state?.from === 'customers'
            ? { label: 'Customers', path: ROUTESPATH.CUSTOMERS }
            : { label: 'Onboarding', path: ROUTESPATH.ONBOARDING },
          { label: 'Configure Discount' },
        ]}
        actions={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={dataLoading}
              onClick={handleExport}
            >
              Export JSON
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={dataLoading || isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </>
        }
      />

      <Card className="mt-6">
        {/* Free Delivery toggle — always visible */}
        <div className="flex items-center gap-3 mb-4">
          <Switch
            checked={freeDelivery}
            loading={patchAccountMutation.isPending}
            onChange={val => {
              setFreeDelivery(val);
              if (accountId) patchAccountMutation.mutate({ id: accountId, dogma_deliverycharge: val });
            }}
          />
          <div>
            <Text strong className="text-sm block">Free Delivery</Text>
            <Text type="secondary" className="text-xs">Enable free delivery for this customer</Text>
          </div>
        </div>

        <Tabs
          activeKey={discountType}
          onChange={key => setDiscountType(key as DiscountType)}
          items={[
            {
              key: 'per_window',
              label: 'Per Window',
              children: (
                <div className="space-y-5 pt-2">
                  {/* Discount Amount switch */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Switch
                      checked={excludingWindowProduct}
                      onChange={setExcludingWindowProduct}
                    />
                    <div>
                      <Text strong className="text-sm block">Discount Amount</Text>
                      <Text type="secondary" className="text-xs">
                        Apply discount to configuration options only — excludes the base window price
                        (e.g. "20% off everything except base price")
                      </Text>
                    </div>
                  </div>

                  <Form layout="vertical">
                    <div className="flex gap-8 flex-wrap">
                      <Form.Item label="Discount %" className="!mb-0">
                        <InputNumber
                          value={fullPercentage}
                          onChange={val => { setFullPercentage(val); if (val != null) setFullAmount(null); }}
                          min={0} max={100} placeholder="0" addonAfter="%"
                          disabled={fullAmount != null}
                          style={{ width: 180 }}
                        />
                      </Form.Item>
                      <Form.Item label="Discount Amount" className="!mb-0">
                        <InputNumber
                          value={fullAmount}
                          onChange={val => { setFullAmount(val); if (val != null) setFullPercentage(null); }}
                          min={0} placeholder="0.00" addonBefore="£"
                          disabled={fullPercentage != null}
                          style={{ width: 180 }}
                        />
                      </Form.Item>
                    </div>
                  </Form>

                  {(fullPercentage != null || fullAmount != null) && (
                    <>
                      <Text type="secondary" className="text-xs block mt-5 mb-2">Example preview</Text>
                      <div className="inline-flex gap-10 items-center flex-wrap px-5 py-4 bg-[#f6ffed] border border-[#b7eb8f] rounded-lg">
                        <div>
                          <Text type="secondary" className="text-xs block">Per Window</Text>
                          <Text strong className="text-lg">£{EXAMPLE_PRICE.toFixed(2)}</Text>
                        </div>
                        <span className="text-xl text-green-500">→</span>
                        <div>
                          <Text type="secondary" className="text-xs block">Price After Discount</Text>
                          <Text strong className="text-lg text-green-500">£{previewAfter?.toFixed(2)}</Text>
                        </div>
                        <div>
                          <Text type="secondary" className="text-xs block">Customer Saves</Text>
                          <Text strong className="text-lg text-orange-500">£{previewSaving.toFixed(2)}</Text>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ),
            },
            {
              key: 'per_configuration',
              label: 'Per Configuration',
              children: (
          <div>
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
              <strong>Discount % </strong> and <strong>Discount Amount</strong> are independent — entering one does not affect the other. Set only the field relevant to this customer.
            </div>
          <Tabs
            defaultActiveKey="products"
            items={[
              {
                key: 'products',
                label: 'Products',
                children: perWindowLoading ? (
                  <div className="text-center p-10"><Spin size="large" /></div>
                ) : activeProducts.length === 0 ? (
                  <Empty description="No active products found." />
                ) : (
                  <Table
                    dataSource={buildProductDiscountRows(activeProducts)}
                    rowKey="_key"
                    pagination={false}
                    size="small"
                    bordered
                    columns={[
                      {
                        title: 'Product',
                        key: 'product',
                        render: (_: any, record: any) => ({
                          children: (
                            <div className="flex items-center gap-2.5">
                              {record.dogma_productimageurl ? (
                                <img src={record.dogma_productimageurl} alt={record.name}
                                  className="w-10 h-10 object-contain rounded bg-gray-50 shrink-0" />
                              ) : (
                                <div className="w-10 h-10 bg-gray-100 rounded shrink-0" />
                              )}
                              <Text strong>{record.name}</Text>
                            </div>
                          ),
                          props: { rowSpan: record._rowType === 'unglazed' ? 0 : record._rowSpan },
                        }),
                      },
                      {
                        title: 'Price Type',
                        key: 'price',
                        width: 150,
                        render: (_: any, record: any) => {
                          const isUnglazed = record._rowType === 'unglazed';
                          const price = isUnglazed ? record.dogma_unglazedprice : record.price;
                          return (
                            <div>
                              <Tag color={isUnglazed ? 'orange' : 'blue'} className="!m-0 mb-1">
                                {isUnglazed ? 'Unglazed' : 'Base'}
                              </Tag>
                              <Text className="block">{price != null ? `£${price}` : '—'}</Text>
                            </div>
                          );
                        },
                      },
                      {
                        title: 'Discount %',
                        key: 'discountPercentage',
                        width: 150,
                        render: (_: any, record: any) => {
                          const isUnglazed = record._rowType === 'unglazed';
                          const basePrice = isUnglazed ? record.dogma_unglazedprice : record.price;
                          const key = isUnglazed ? `${record.productid}::unglazed` : record.productid;
                          if (basePrice == null) return <Text type="secondary">—</Text>;
                          const d = itemDiscounts[key] || {};
                          const computedPct = d.priceAfterDiscount != null && basePrice
                            ? +(((basePrice - d.priceAfterDiscount) / basePrice) * 100).toFixed(2)
                            : d.discountAmount != null && basePrice
                              ? +((d.discountAmount / basePrice) * 100).toFixed(2)
                              : null;
                          return (
                            <InputNumber
                              value={d.discountPercentage ?? computedPct}
                              onChange={val => setDiscountPct(key, val)}
                              min={0} max={100} placeholder="0" addonAfter="%"
                              disabled={d.discountAmount != null || d.priceAfterDiscount != null}
                              status={(d.discountPercentage ?? 0) > 100 ? 'error' : undefined}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                      {
                        title: 'Discount Amount',
                        key: 'discountAmount',
                        width: 170,
                        render: (_: any, record: any) => {
                          const isUnglazed = record._rowType === 'unglazed';
                          const basePrice = isUnglazed ? record.dogma_unglazedprice : record.price;
                          const key = isUnglazed ? `${record.productid}::unglazed` : record.productid;
                          if (basePrice == null) return <Text type="secondary">—</Text>;
                          const d = itemDiscounts[key] || {};
                          const computedAmt = d.discountPercentage != null && basePrice != null
                            ? +((basePrice * d.discountPercentage) / 100).toFixed(2) : null;
                          return (
                            <InputNumber
                              value={d.discountAmount ?? computedAmt}
                              onChange={val => setDiscountAmt(key, val)}
                              min={0} placeholder="0.00" addonBefore="£"
                              disabled={d.discountPercentage != null || d.priceAfterDiscount != null}
                              status={(d.discountAmount ?? 0) > (basePrice ?? Infinity) ? 'error' : undefined}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                      {
                        title: 'Price After Discount',
                        key: 'priceAfter',
                        width: 170,
                        render: (_: any, record: any) => {
                          const isUnglazed = record._rowType === 'unglazed';
                          const basePrice = isUnglazed ? record.dogma_unglazedprice : record.price;
                          const key = isUnglazed ? `${record.productid}::unglazed` : record.productid;
                          if (basePrice == null) return <Text type="secondary">—</Text>;
                          const d = itemDiscounts[key] || {};
                          const isReadOnly = d.discountPercentage != null;
                          const displayVal = d.priceAfterDiscount != null
                            ? d.priceAfterDiscount
                            : (d.discountAmount != null || d.discountPercentage != null) ? calcDiscountedPrice(basePrice, d) : null;
                          return (
                            <InputNumber
                              value={displayVal}
                              onChange={val => setDiscountPAD(key, val, basePrice)}
                              min={0} placeholder="0.00" addonBefore="£"
                              disabled={isReadOnly}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'config_options',
                label: 'Configuration Options',
                children: perOptionsLoading ? (
                  <div className="text-center p-10"><Spin size="large" /></div>
                ) : pricedOptions.length === 0 ? (
                  <Empty description="No priced configuration options found." />
                ) : (
                  <Tabs
                    type="line"
                    size="small"
                    className="pt-1"
                    items={Object.entries(optionsByCategory).map(([catId, catOptions]) => {
                      // Build grouped data source — header row before each optionType group
                      const grouped = new Map<number, any[]>();
                      for (const opt of catOptions as any[]) {
                        const t = opt.dogma_optiontype ?? 0;
                        if (!grouped.has(t)) grouped.set(t, []);
                        grouped.get(t)!.push(opt);
                      }
                      const groupedDataSource: (any | IGroupHeaderRow)[] = [];
                      for (const [type, items] of [...grouped.entries()].sort((a, b) => a[0] - b[0])) {
                        groupedDataSource.push({ isGroupHeader: true, optionType: type, catId, groupItems: items, _key: `group-${catId}-${type}` });
                        groupedDataSource.push(...items);
                      }

                      const COL_COUNT = 5;

                      return {
                        key: catId,
                        label: categoryMap[catId] || 'Uncategorised',
                        children: (
                          <Table
                            dataSource={groupedDataSource}
                            rowKey={(r: any) => r._key ?? r.dogma_configurationoptionsid}
                            pagination={false}
                            size="small"
                            bordered
                            onRow={(record: any) =>
                              record.isGroupHeader ? { className: 'bg-blue-50' } : {}
                            }
                            expandable={{
                              defaultExpandAllRows: true,
                              rowExpandable: (record: any) => !record.isGroupHeader && parsePriceList(record.dogma_pricelist).length > 0,
                              expandedRowRender: (record: any) => {
                                const parentDiscount = itemDiscounts[record.dogma_configurationoptionsid] || {};
                                const parentHasDiscount = parentDiscount.discountPercentage != null || (parentDiscount.discountAmount != null && parentDiscount.priceAfterDiscount == null);
                                return (
                                  <Table
                                    dataSource={parsePriceList(record.dogma_pricelist).map((e, i) => ({ ...e, _key: i }))}
                                    rowKey="_key"
                                    size="small"
                                    pagination={false}
                                    className="my-2"
                                    columns={[
                                      {
                                        title: 'Conditions',
                                        key: 'conditions',
                                        render: (_: any, row: any) => renderConditions(row.conditions),
                                      },
                                      {
                                        title: 'Price',
                                        key: 'price',
                                        width: 100,
                                        render: (_: any, row: any) => <Text>£{row.price}</Text>,
                                      },
                                      {
                                        title: 'Discount %',
                                        key: 'discountPercentage',
                                        width: 140,
                                        render: (_: any, row: any) => {
                                          if (parentHasDiscount) return <Text type="secondary">—</Text>;
                                          const plKey = `${record.dogma_configurationoptionsid}::pl::${row._key}`;
                                          const d = itemDiscounts[plKey] || {};
                                          const computedPct = d.priceAfterDiscount != null && row.price
                                            ? +(((row.price - d.priceAfterDiscount) / row.price) * 100).toFixed(2)
                                            : d.discountAmount != null && row.price
                                              ? +((d.discountAmount / row.price) * 100).toFixed(2)
                                              : null;
                                          return (
                                            <InputNumber
                                              value={d.discountPercentage ?? computedPct}
                                              onChange={val => setDiscountPct(plKey, val)}
                                              min={0} max={100} placeholder="0" addonAfter="%"
                                              disabled={d.discountAmount != null || d.priceAfterDiscount != null}
                                              style={{ width: '100%' }}
                                            />
                                          );
                                        },
                                      },
                                      {
                                        title: 'Discount Amount',
                                        key: 'discountAmount',
                                        width: 160,
                                        render: (_: any, row: any) => {
                                          if (parentHasDiscount) return <Text type="secondary">—</Text>;
                                          const plKey = `${record.dogma_configurationoptionsid}::pl::${row._key}`;
                                          const d = itemDiscounts[plKey] || {};
                                          const computedAmt = d.discountPercentage != null && row.price
                                            ? +((row.price * d.discountPercentage) / 100).toFixed(2) : null;
                                          return (
                                            <InputNumber
                                              value={d.discountAmount ?? computedAmt}
                                              onChange={val => setDiscountAmt(plKey, val)}
                                              min={0} placeholder="0.00" addonBefore="£"
                                              disabled={d.discountPercentage != null || d.priceAfterDiscount != null}
                                              style={{ width: '100%' }}
                                            />
                                          );
                                        },
                                      },
                                      {
                                        title: 'Price After Discount',
                                        key: 'priceAfter',
                                        width: 170,
                                        render: (_: any, row: any) => {
                                          if (parentHasDiscount) {
                                            const after = calcDiscountedPrice(row.price, parentDiscount);
                                            return after != null
                                              ? <Tag color="green">£{after.toFixed(2)}</Tag>
                                              : <Text type="secondary">—</Text>;
                                          }
                                          const plKey = `${record.dogma_configurationoptionsid}::pl::${row._key}`;
                                          const d = itemDiscounts[plKey] || {};
                                          const isReadOnly = d.discountPercentage != null;
                                          const displayVal = d.priceAfterDiscount != null
                                            ? d.priceAfterDiscount
                                            : (d.discountAmount != null || d.discountPercentage != null) ? calcDiscountedPrice(row.price, d) : null;
                                          return (
                                            <InputNumber
                                              value={displayVal}
                                              onChange={val => setDiscountPAD(plKey, val, row.price)}
                                              min={0} placeholder="0.00" addonBefore="£"
                                              disabled={isReadOnly}
                                              style={{ width: '100%' }}
                                            />
                                          );
                                        },
                                      },
                                    ]}
                                  />
                                );
                              },
                            }}
                            columns={[
                              {
                                title: 'Option',
                                key: 'option',
                                render: (_: any, record: any) => {
                                  if (record.isGroupHeader) {
                                    const optType = ALL_OPTION_TYPES.find(t => t.value === record.optionType);
                                    return {
                                      children: (
                                        <div className="flex items-center gap-2 py-0.5">
                                          <Tag color="geekblue" className="font-semibold text-xs m-0">
                                            {optType?.label || `Type ${record.optionType}`}
                                          </Tag>
                                        </div>
                                      ),
                                      props: { colSpan: COL_COUNT },
                                    };
                                  }
                                  return (
                                    <div className="flex items-center gap-2.5">
                                      {record.dogma_picturebloburl ? (
                                        <img src={record.dogma_picturebloburl} alt={record.dogma_name}
                                          className="w-10 h-10 object-contain rounded bg-gray-50 shrink-0" />
                                      ) : (
                                        <div className="w-10 h-10 bg-gray-100 rounded shrink-0" />
                                      )}
                                      <div>
                                        <Text strong className="block">{record.dogma_name}</Text>
                                        {parsePriceList(record.dogma_pricelist).length > 0 && (
                                          <Text type="secondary" className="text-[11px]">
                                            {parsePriceList(record.dogma_pricelist).length} conditional price(s) — expand to view
                                          </Text>
                                        )}
                                      </div>
                                    </div>
                                  );
                                },
                              },
                              {
                                title: 'Price Modifier',
                                key: 'price',
                                width: 130,
                                render: (_: any, record: any) => {
                                  if (record.isGroupHeader) return { props: { colSpan: 0 } };
                                  return <Text>{record.dogma_pricemodifier != null ? `£${record.dogma_pricemodifier}` : '—'}</Text>;
                                },
                              },
                              {
                                title: 'Discount %',
                                key: 'discountPercentage',
                                width: 150,
                                render: (_: any, record: any) => {
                                  if (record.isGroupHeader) return { props: { colSpan: 0 } };
                                  const hasPriceList = parsePriceList(record.dogma_pricelist).length > 0;
                                  const noBasePrice = !record.dogma_pricemodifier;
                                  if (hasPriceList && noBasePrice) return <Text type="secondary" className="text-xs">—</Text>;
                                  const d = itemDiscounts[record.dogma_configurationoptionsid] || {};
                                  const computedPct = d.priceAfterDiscount != null && record.dogma_pricemodifier
                                    ? +(((record.dogma_pricemodifier - d.priceAfterDiscount) / record.dogma_pricemodifier) * 100).toFixed(2)
                                    : d.discountAmount != null && record.dogma_pricemodifier
                                      ? +((d.discountAmount / record.dogma_pricemodifier) * 100).toFixed(2)
                                      : null;
                                  return (
                                    <InputNumber
                                      value={d.discountPercentage ?? computedPct}
                                      onChange={val => setDiscountPct(record.dogma_configurationoptionsid, val)}
                                      min={0} max={100} placeholder="0" addonAfter="%"
                                      disabled={d.discountAmount != null || d.priceAfterDiscount != null}
                                      status={(d.discountPercentage ?? 0) > 100 ? 'error' : undefined}
                                      style={{ width: '100%' }}
                                    />
                                  );
                                },
                              },
                              {
                                title: 'Discount Amount',
                                key: 'discountAmount',
                                width: 170,
                                render: (_: any, record: any) => {
                                  if (record.isGroupHeader) return { props: { colSpan: 0 } };
                                  const hasPriceList = parsePriceList(record.dogma_pricelist).length > 0;
                                  const noBasePrice = !record.dogma_pricemodifier;
                                  if (hasPriceList && noBasePrice) return <Text type="secondary" className="text-xs">—</Text>;
                                  const d = itemDiscounts[record.dogma_configurationoptionsid] || {};
                                  const computedAmt = d.discountPercentage != null && record.dogma_pricemodifier
                                    ? +((record.dogma_pricemodifier * d.discountPercentage) / 100).toFixed(2) : null;
                                  return (
                                    <InputNumber
                                      value={d.discountAmount ?? computedAmt}
                                      onChange={val => setDiscountAmt(record.dogma_configurationoptionsid, val)}
                                      min={0} placeholder="0.00" addonBefore="£"
                                      disabled={d.discountPercentage != null || d.priceAfterDiscount != null}
                                      status={(() => {
                                        const max = getEffectiveMaxPrice(record);
                                        return (max !== null && (d.discountAmount ?? 0) > max) ? 'error' : undefined;
                                      })()}
                                      style={{ width: '100%' }}
                                    />
                                  );
                                },
                              },
                              {
                                title: 'Price After Discount',
                                key: 'priceAfter',
                                width: 170,
                                render: (_: any, record: any) => {
                                  if (record.isGroupHeader) return { props: { colSpan: 0 } };
                                  // If all prices are in pricelist (priceModifier is 0/null or pricelist exists), defer to expanded rows
                                  const hasPriceList = parsePriceList(record.dogma_pricelist).length > 0;
                                  if (hasPriceList) {
                                    return <Text type="secondary" className="text-xs italic">Expand to set per price</Text>;
                                  }
                                  const base = record.dogma_pricemodifier;
                                  if (!base) return <Text type="secondary">—</Text>;
                                  const d = itemDiscounts[record.dogma_configurationoptionsid] || {};
                                  const isReadOnly = d.discountPercentage != null;
                                  const displayVal = d.priceAfterDiscount != null
                                    ? d.priceAfterDiscount
                                    : (d.discountAmount != null || d.discountPercentage != null) ? calcDiscountedPrice(base, d) : null;
                                  return (
                                    <InputNumber
                                      value={displayVal}
                                      onChange={val => setDiscountPAD(record.dogma_configurationoptionsid, val, base)}
                                      min={0} placeholder="0.00" addonBefore="£"
                                      disabled={isReadOnly}
                                      style={{ width: '100%' }}
                                    />
                                  );
                                },
                              },
                            ]}
                          />
                        ),
                      };
                    })}
                  />
                ),
              },
              {
                key: 'pricing_rules',
                label: 'Pricing Rules',
                children: pricingRules.length === 0 ? (
                  <Empty description="No pricing rules found in live configuration." />
                ) : (
                  <Table
                    dataSource={pricingRules}
                    rowKey="dogma_pricingruleid"
                    pagination={false}
                    size="small"
                    bordered
                    columns={[
                      {
                        title: 'Rule Name',
                        key: 'name',
                        render: (_: any, record: IPricingRule) => (
                          <div>
                            <Text strong>{record.dogma_name}</Text>
                            <Text type="secondary" className="text-xs block">
                              {record.dogma_operation === 'multiply' ? `×${record.dogma_price_value}` : `+£${record.dogma_price_value}`}
                              {record.dogma_apply_per_unit ? ' · per unit' : ''}
                              {record.dogma_apply_per_sash ? ' · per sash' : ''}
                              {record.dogma_apply_per_metre ? ' · per metre' : ''}
                            </Text>
                          </div>
                        ),
                      },
                      {
                        title: 'Price Value',
                        key: 'price_value',
                        width: 120,
                        render: (_: any, record: IPricingRule) => (
                          <Text>{record.dogma_operation === 'add' ? `£${record.dogma_price_value}` : `×${record.dogma_price_value}`}</Text>
                        ),
                      },
                      {
                        title: 'Discount %',
                        key: 'discountPercentage',
                        width: 150,
                        render: (_: any, record: IPricingRule) => {
                          const key = `pr::${record.dogma_pricingruleid}`;
                          const d = itemDiscounts[key] || {};
                          const computedPct = d.discountAmount != null && record.dogma_price_value
                            ? +((d.discountAmount / record.dogma_price_value) * 100).toFixed(2)
                            : null;
                          return (
                            <InputNumber
                              value={d.discountPercentage ?? computedPct}
                              onChange={val => setDiscountPct(key, val)}
                              min={0} max={100} placeholder="0" addonAfter="%"
                              disabled={d.discountAmount != null}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                      {
                        title: 'Discount Amount',
                        key: 'discountAmount',
                        width: 170,
                        render: (_: any, record: IPricingRule) => {
                          if (record.dogma_operation === 'multiply') {
                            return <Text type="secondary" className="text-xs">Use % for multiply rules</Text>;
                          }
                          const key = `pr::${record.dogma_pricingruleid}`;
                          const d = itemDiscounts[key] || {};
                          const computedAmt = d.discountPercentage != null
                            ? +((record.dogma_price_value * d.discountPercentage) / 100).toFixed(2)
                            : null;
                          return (
                            <InputNumber
                              value={d.discountAmount ?? computedAmt}
                              onChange={val => setDiscountAmt(key, val)}
                              min={0} placeholder="0.00" addonBefore="£"
                              disabled={d.discountPercentage != null}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                      {
                        title: 'Price After Discount',
                        key: 'priceAfter',
                        width: 170,
                        render: (_: any, record: IPricingRule) => {
                          if (record.dogma_operation === 'multiply') {
                            const key = `pr::${record.dogma_pricingruleid}`;
                            const d = itemDiscounts[key] || {};
                            if (d.discountPercentage != null) {
                              const after = +(record.dogma_price_value * (1 - d.discountPercentage / 100)).toFixed(4);
                              return <Tag color="green">×{after}</Tag>;
                            }
                            return <Text type="secondary">—</Text>;
                          }
                          const base = record.dogma_price_value;
                          const key = `pr::${record.dogma_pricingruleid}`;
                          const d = itemDiscounts[key] || {};
                          const isReadOnly = d.discountPercentage != null;
                          const displayVal = d.priceAfterDiscount != null
                            ? d.priceAfterDiscount
                            : (d.discountAmount != null || d.discountPercentage != null)
                              ? calcDiscountedPrice(base, d)
                              : null;
                          return (
                            <InputNumber
                              value={displayVal}
                              onChange={val => setDiscountPAD(key, val, base)}
                              min={0} placeholder="0.00" addonBefore="£"
                              disabled={isReadOnly}
                              style={{ width: '100%' }}
                            />
                          );
                        },
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
          </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default CustomerDiscountPage;
